import { FC, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";

import { EventsTrainSortColumn } from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { SemaforIcon } from "../../../../../../utils";
import { SortButton } from "../../../../../../components/common/buttons/sort-button";
import { StyledTablePagination } from "../../ui/table-pagination";
import { TABLE_BG_COLOR, TABLE_BORDER_COLOR } from "./constants";
import { IEventExportRow } from "./interfaces";
import { StyledHeaderTableCell, StyledTableCell } from "./styled";

export interface ITrainEventsTable {
  rows: IEventExportRow[];
  rawEventsMap: Map<number, { Flags?: number; idEvent?: number }>;
  handleSort: (col: EventsTrainSortColumn) => () => void;
  sortColumn: EventsTrainSortColumn;
  sortOrder: SortDirection;
  embedded?: boolean;
  onAckClick: (idEvent: number) => void;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newTake: number) => void;
  isServerPagination?: boolean;
}

export const TrainEventsTable: FC<ITrainEventsTable> = props => {
  const {
    rows,
    rawEventsMap,
    handleSort,
    sortColumn,
    sortOrder,
    embedded,
    onAckClick,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    isServerPagination,
  } = props;

  const { formatMessage } = useIntl();

  const eventsLogId = "pages.eventsLog";
  const headersId = `${eventsLogId}.messages.headers`;

  const visibleRows = useMemo(
    () =>
      !isServerPagination && rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows,
    [rows, page, rowsPerPage, isServerPagination],
  );

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = Math.max(0, rowsPerPage - visibleRows.length);

  const isSortable = (col: EventsTrainSortColumn) => ({
    direction: sortOrder,
    onClick: handleSort(col),
    isActive: sortColumn === col,
    width: 12,
    height: 12,
  });

  return (
    <TableContainer
      elevation={0}
      component={Paper}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        borderRadius: "8px",
        border: `1px solid ${TABLE_BORDER_COLOR}`,
      }}
    >
      <Table sx={{ minWidth: 900, width: "100%" }} aria-label="train events table" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              height: embedded ? 29 : 53,
              "& .MuiTableCell-root": {
                borderRight: `1px solid ${TABLE_BORDER_COLOR}`,
                backgroundColor: TABLE_BG_COLOR,
                borderColor: TABLE_BORDER_COLOR,
                "&:last-of-type": { borderRight: "none" },
              },
            }}
          >
            <StyledHeaderTableCell sx={{ minWidth: 50 }}>
              {formatMessage({ id: `${headersId}.ackStatus` })}
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 50 }}>
              {formatMessage({ id: `${headersId}.status` })}
              <SortButton {...isSortable(EventsTrainSortColumn.DataAlarmStatus)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 120 }}>
              {formatMessage({ id: `${headersId}.eventType` })}
              <SortButton {...isSortable(EventsTrainSortColumn.EventType)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 120 }}>
              {formatMessage({ id: `${headersId}.point` })}
              <SortButton {...isSortable(EventsTrainSortColumn.PointName)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 150 }}>
              {formatMessage({ id: `${headersId}.dateTime` })}
              <SortButton {...isSortable(EventsTrainSortColumn.EventLogDate)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 200 }}>
              {formatMessage({ id: `${headersId}.parameter` })}
              <SortButton {...isSortable(EventsTrainSortColumn.MeasureType)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 100 }}>
              {formatMessage({ id: `${headersId}.source` })}
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 150 }}>
              {formatMessage({ id: `${headersId}.fullName` })}
              <SortButton {...isSortable(EventsTrainSortColumn.UserLogin)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 400 }}>
              {formatMessage({ id: `${headersId}.message` })}
              <SortButton {...isSortable(EventsTrainSortColumn.DataValue1)} />
            </StyledHeaderTableCell>

            <StyledHeaderTableCell sx={{ minWidth: 150 }}>
              {formatMessage({ id: `${headersId}.defect` })}
            </StyledHeaderTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {visibleRows.map(row => {
            const rawEvent = row.idEvent != null ? rawEventsMap.get(row.idEvent) : undefined;
            const flags = rawEvent?.Flags ?? 0;
            const isChecked = flags === 1 || flags === 2;
            const isDisabled = flags !== 0;

            return (
              <TableRow
                key={row.idEvent}
                sx={{
                  height: embedded ? 29 : 53,
                  "& .MuiTableCell-root": {
                    borderColor: TABLE_BORDER_COLOR,
                    borderRight: `1px solid ${TABLE_BORDER_COLOR}`,
                    "&:last-of-type": { borderRight: "none" },
                  },
                }}
              >
                {/* ackStatus */}
                <StyledTableCell sx={{ minWidth: 50, maxWidth: 50 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                    <Checkbox
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => {
                        if (!isDisabled && rawEvent?.idEvent != null) {
                          onAckClick(rawEvent.idEvent);
                        }
                      }}
                      size="small"
                      sx={{ padding: 0 }}
                    />
                  </Box>
                </StyledTableCell>

                {/* status */}
                <StyledTableCell sx={{ minWidth: 50, maxWidth: 50 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                    <SemaforIcon
                      status={Number(row.status) || 0}
                      width={embedded ? 8 : 12}
                      height={embedded ? 8 : 12}
                    />
                  </Box>
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 120, maxWidth: 120 }} title={row.type ?? ""}>
                  {row.type ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 120, maxWidth: 120 }} title={row.pointName ?? ""}>
                  {row.pointName ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 150, maxWidth: 150 }} title={row.date ?? ""}>
                  {row.date ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 200, maxWidth: 200 }} title={row.parameter ?? ""}>
                  {row.parameter ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 100, maxWidth: 100 }} title={row.source ?? ""}>
                  {row.source ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 150, maxWidth: 150 }} title={row.fullName ?? ""}>
                  {row.fullName ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 400, maxWidth: 400 }} title={row.message ?? ""}>
                  {row.message ?? ""}
                </StyledTableCell>

                <StyledTableCell sx={{ minWidth: 150, maxWidth: 150 }} title={row.defect ?? ""}>
                  {row.defect ?? ""}
                </StyledTableCell>
              </TableRow>
            );
          })}

          {emptyRows > 0 && (
            <TableRow sx={{ height: (embedded ? 29 : 53) * emptyRows }}>
              <TableCell colSpan={10} />
            </TableRow>
          )}
        </TableBody>

        {totalCount > 0 && (
          <TableFooter>
            <TableRow
              sx={{
                "& .MuiToolbar-root.MuiTablePagination-toolbar": { minHeight: 30, fontSize: 12 },
                "& .MuiToolbar-root.MuiTablePagination-toolbar p": { fontSize: 12, m: 0 },
                "& .MuiToolbar-root .MuiButtonBase-root": { padding: 0.5 },
              }}
            >
              <StyledTablePagination
                onPageChange={(_e, newPage) => onPageChange(newPage)}
                onRowsPerPageChange={e => onRowsPerPageChange(parseInt(e.target.value, 10))}
                page={page}
                rowsLength={totalCount}
                rowsPerPage={rowsPerPage}
                colSpan={10}
                showAllOption={false}
                rowsPerPageOptions={embedded ? [5, 15, 25] : [25]}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};
